import { SERVICE_TOKEN } from "./lib/apiConfig";
import { useState, useEffect, useRef } from "react";
import BottleScanView from "./components/BottleScanView";
import WTRBottleScreen from "./components/WTRBottleScreen";
import WTRHubScreen from "./components/WTRHubScreen";
import ProfileScreen from "./components/ProfileScreen";
import MyIntake from "./components/MyIntake";
import MyIntakeScreen from "./components/MyIntakeScreen";
import PaywallScreen from "./components/PaywallScreen";
import DevicesScreen from "./components/DevicesScreen";
import MyHealth from "./components/MyHealth";
import ReportsScreen from "./components/ReportsScreen";
import HelpScreen from "./components/HelpScreen";
import ShopScreen from "./components/ShopScreen";
import AuthScreen from "./components/AuthScreen";
import { consumeAuthTokenFromUrl, installAuthGuard } from "./lib/auth";
import { requestPushPermission, registerServiceWorker } from './push';

// Design system primitives
import Icon from "./components/ds/Icon";
import NavIcon from "./components/ds/NavIcon";
import Input from "./components/ds/Input";
import SegmentedToggle from "./components/ds/SegmentedToggle";
import ContaminantCard from "./components/ds/ContaminantCard";
import HeroCard from "./components/ds/HeroCard";

// TBV-specific components
import RiskGauge from "./components/tbv/RiskGauge";
import WTRHubAnimation from "./components/tbv/WTRHubAnimation";
import HealthCalc from "./components/tbv/HealthCalc";
import MonthlyReportModal from "./components/tbv/MonthlyReportModal";
import { currentTier, refreshTier } from "./lib/entitlements";
import UtilitySelector from "./components/tbv/UtilitySelector";
import MyHydrationScreen from "./components/MyHydrationScreen";
import NavDrawer from "./components/NavDrawer";
import AppHeader from "./components/AppHeader";
import WaterWatchFeed from "./components/WaterWatchFeed";

// --- ORACLE REPORT TRANSFORM -------------------------------------------------
function transformOracleReport(report, cityLabel, zip) {
  const contaminants = (report.contaminants || []).map(c => {
    function fmtNum(val) {
      if (val == null || isNaN(val)) return null;
      const n = parseFloat(val);
      if (isNaN(n)) return null;
      const abs = Math.abs(n);
      if (n === 0) return '0';
      if (abs < 0.0001) return 'Trace';
      if (abs < 0.01)   return parseFloat(n.toFixed(4)).toString();
      if (abs < 1)      return parseFloat(n.toFixed(4)).toString();
      if (abs < 1000)   return parseFloat(n.toFixed(2)).toString();
      return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
    }
    const formattedLevel    = fmtNum(c.level);
    const formattedGuideline = fmtNum(c.ewg_guideline);
    const ewgExceedance = (c.level && c.ewg_guideline && c.ewg_guideline > 0)
      ? parseFloat((c.level / c.ewg_guideline).toFixed(1))
      : null;
    return {
      code:           c.code,
      name:           c.name,
      group:          c.group || c.category,
      category:       c.category,
      icon:           'warn',
      risk:           c.risk,
      riskColor:      c.risk,
      riskLevel:      c.risk,
      level:          formattedLevel,
      detected:       formattedLevel,
      unit:           c.unit,
      limit:          c.limit,
      epaMcl:         c.limit,
      ewgGuideline:   formattedGuideline,
      ewg_guideline:  formattedGuideline,
      ewgExceedance,
      exceedsMcl:     c.limit != null ? (c.level > c.limit) : false,
      exceedsEwg:     c.ewg_guideline != null ? (c.level > c.ewg_guideline) : false,
      isViolation:    c.is_violation,
      detail:         c.detail,
      healthShort:    c.detail,
      healthLong:     c.health_effects_long,
      vulnerable:     c.vulnerable_populations || [],
      carcinogenClass: c.is_carcinogen ? 'Group 1' : null,
      isRegulated:    c.limit != null,
      hasNoSafeLevel: c.has_no_safe_level,
      hubRemoves:     c.removed,
      hubRemovalPct:  c.removed ? 99 : null,
      removed:        c.removed,
    };
  });

  return {
    city:             cityLabel,
    utility:          report.utility,
    source:           report.source || 'Municipal',
    zip,
    tds:              report.tds || null,
    ph:               report.ph || null,
    hardness:         report.hardness || null,
    contaminants,
    riskScore:        report.risk_score ?? getRiskScore(contaminants),
    waterThreatScore: report.risk_score ?? 0,
    dataConfidence:   report.data_confidence || 0,
    populationServed: report.population_served || null,
  };
}

// --- BOTTLE BRANDS DATA -----------------------------------------------------
// eslint-disable-next-line no-unused-vars
const BOTTLE_BRANDS = {
  "Evian":{ origin:"French Alps", tds:345, ph:7.2, fluoride:0.1, microplastics:"HIGH", pfas_risk:"MEDIUM", score:62, concern:"High TDS. Microplastic contamination in independent testing.", manufacturer:"Danone S.A.", source_type:"Natural mineral water" },
  "Dasani":{ origin:"Municipal tap (filtered)", tds:38, ph:5.6, fluoride:0.07, microplastics:"VERY HIGH", pfas_risk:"HIGH", score:78, concern:"Acidic pH 5.6, PFAS-lined packaging, highest microplastic count in 2023 Orb Media study.", manufacturer:"The Coca-Cola Company", source_type:"Purified water" },
  "Aquafina":{ origin:"Municipal tap (RO)", tds:11, ph:6.0, fluoride:0.05, microplastics:"HIGH", pfas_risk:"HIGH", score:71, concern:"Ultra-low minerals, acidic, PFAS in packaging migration studies.", manufacturer:"PepsiCo Inc.", source_type:"Purified water" },
  "Poland Spring":{ origin:"Maine springs", tds:37, ph:7.2, fluoride:0.1, microplastics:"HIGH", pfas_risk:"MEDIUM", score:55, concern:"High microplastic count. FTC settlement over 'natural spring' claims.", manufacturer:"BlueTriton Brands", source_type:"Spring water" },
  "Fiji Water":{ origin:"Artesian aquifer, Fiji", tds:222, ph:7.7, fluoride:0.2, microplastics:"MEDIUM", pfas_risk:"LOW", score:38, concern:"Arsenic near WHO limits. 18,000-mile carbon footprint.", manufacturer:"The Wonderful Company", source_type:"Artesian water" },
  "Smart Water":{ origin:"Municipal tap (distilled)", tds:24, ph:7.0, fluoride:0.0, microplastics:"MEDIUM", pfas_risk:"MEDIUM", score:44, concern:"No natural minerals. Electrolytes added post-distillation.", manufacturer:"The Coca-Cola Company", source_type:"Vapor distilled" },
  "Voss":{ origin:"Artesian aquifer, Norway", tds:44, ph:6.0, fluoride:0.0, microplastics:"LOW", pfas_risk:"LOW", score:29, concern:"Slightly acidic. Glass bottle option recommended.", manufacturer:"Voss of Norway ASA", source_type:"Artesian water" },
  "Nestle Pure Life":{ origin:"Municipal tap (filtered)", tds:45, ph:6.8, fluoride:0.08, microplastics:"HIGH", pfas_risk:"MEDIUM", score:58, concern:"Multiple source locations with varying quality. Now owned by BlueTriton.", manufacturer:"BlueTriton Brands", source_type:"Purified water" },
  "Arrowhead":{ origin:"California springs", tds:190, ph:7.8, fluoride:0.1, microplastics:"MEDIUM", pfas_risk:"MEDIUM", score:48, concern:"High arsenic levels reported in some batches. Water rights controversy.", manufacturer:"BlueTriton Brands", source_type:"Mountain spring water" },
  "Crystal Geyser":{ origin:"Natural springs (CA/TN)", tds:95, ph:6.9, fluoride:0.1, microplastics:"MEDIUM", pfas_risk:"LOW", score:35, concern:"Arsenic violations in 2018. Bottled at multiple springs.", manufacturer:"CG Roxane LLC", source_type:"Natural alpine spring" },
  "Deer Park":{ origin:"Mid-Atlantic springs", tds:52, ph:6.6, fluoride:0.04, microplastics:"HIGH", pfas_risk:"MEDIUM", score:52, concern:"Slightly acidic. Owned by Nestle until 2021.", manufacturer:"BlueTriton Brands", source_type:"Spring water" },
  "Ice Mountain":{ origin:"Great Lakes region springs", tds:35, ph:7.4, fluoride:0.02, microplastics:"HIGH", pfas_risk:"MEDIUM", score:50, concern:"Water extraction controversy in Michigan.", manufacturer:"BlueTriton Brands", source_type:"Spring water" },
  "Ozarka":{ origin:"Texas springs", tds:200, ph:7.5, fluoride:0.15, microplastics:"HIGH", pfas_risk:"MEDIUM", score:54, concern:"Higher mineral content. Regional Texas brand.", manufacturer:"BlueTriton Brands", source_type:"Spring water" },
  "Zephyrhills":{ origin:"Florida springs", tds:180, ph:7.7, fluoride:0.06, microplastics:"MEDIUM", pfas_risk:"MEDIUM", score:45, concern:"Florida aquifer source. Moderate mineral content.", manufacturer:"BlueTriton Brands", source_type:"Spring water" },
  "Essentia":{ origin:"Municipal tap (ionized)", tds:50, ph:9.5, fluoride:0.0, microplastics:"LOW", pfas_risk:"LOW", score:32, concern:"Alkaline ionized water. No proven health benefits over regular water.", manufacturer:"Essentia Water LLC", source_type:"Ionized alkaline water" },
  "Core":{ origin:"Municipal tap (filtered)", tds:20, ph:7.4, fluoride:0.0, microplastics:"LOW", pfas_risk:"LOW", score:34, concern:"Ultra-purified with added electrolytes. Low mineral content.", manufacturer:"Core Nutrition LLC", source_type:"Nutrient enhanced water" },
  "Lifewtr":{ origin:"Municipal tap (filtered)", tds:15, ph:6.8, fluoride:0.0, microplastics:"MEDIUM", pfas_risk:"MEDIUM", score:46, concern:"Electrolyte-added purified water. Minimal minerals.", manufacturer:"PepsiCo Inc.", source_type:"Purified water" },
  "Propel":{ origin:"Municipal tap (filtered)", tds:30, ph:3.5, fluoride:0.0, microplastics:"MEDIUM", pfas_risk:"MEDIUM", score:65, concern:"Very acidic pH 3.5. Contains artificial sweeteners.", manufacturer:"PepsiCo Inc.", source_type:"Flavored fitness water" },
  "Hint":{ origin:"Municipal tap (filtered)", tds:12, ph:6.5, fluoride:0.0, microplastics:"MEDIUM", pfas_risk:"LOW", score:40, concern:"Fruit-infused purified water. Natural flavors only.", manufacturer:"Hint Inc.", source_type:"Fruit-infused water" },
  "La Croix":{ origin:"Municipal tap (carbonated)", tds:5, ph:4.5, fluoride:0.0, microplastics:"LOW", pfas_risk:"LOW", score:38, concern:"Sparkling water. Acidic due to carbonation. May affect tooth enamel.", manufacturer:"National Beverage Corp.", source_type:"Sparkling water" },
  "Perrier":{ origin:"Vergeze, France", tds:475, ph:5.5, fluoride:0.1, microplastics:"LOW", pfas_risk:"LOW", score:36, concern:"Natural carbonation. High mineral content. Slightly acidic.", manufacturer:"Nestle Waters", source_type:"Sparkling natural mineral" },
  "San Pellegrino":{ origin:"San Pellegrino Terme, Italy", tds:950, ph:7.7, fluoride:0.4, microplastics:"LOW", pfas_risk:"LOW", score:33, concern:"Very high TDS. Not recommended for daily hydration.", manufacturer:"Nestle Waters", source_type:"Sparkling natural mineral" },
  "Mountain Valley":{ origin:"Arkansas springs", tds:220, ph:7.8, fluoride:0.1, microplastics:"LOW", pfas_risk:"LOW", score:28, concern:"Premium spring water. Glass bottles available. Low contamination.", manufacturer:"Mountain Valley Spring Co.", source_type:"Spring water" },
  "Icelandic Glacial":{ origin:"Olfus Spring, Iceland", tds:62, ph:8.4, fluoride:0.0, microplastics:"LOW", pfas_risk:"LOW", score:25, concern:"Naturally alkaline. One of the purest commercial waters.", manufacturer:"Icelandic Glacial Inc.", source_type:"Natural spring water" },
};

// --- UPC BARCODE DATABASE ----------------------------------------------------
// eslint-disable-next-line no-unused-vars
const UPC_DATABASE = {
  "049000006346": "Dasani", "049000028904": "Dasani", "049000028911": "Dasani",
  "049000006360": "Dasani", "049000042726": "Dasani", "049000072112": "Dasani",
  "012000001307": "Aquafina", "012000001314": "Aquafina", "012000001321": "Aquafina",
  "012000161148": "Aquafina", "012000001291": "Aquafina", "012000204173": "Aquafina",
  "061314000011": "Evian", "061314000028": "Evian", "061314000035": "Evian",
  "079298612417": "Evian", "061314000073": "Evian", "079298612004": "Evian",
  "075720004010": "Poland Spring", "075720004034": "Poland Spring", "075720004058": "Poland Spring",
  "075720400010": "Poland Spring", "075720400034": "Poland Spring", "075720202034": "Poland Spring",
  "632565000012": "Fiji Water", "632565000029": "Fiji Water", "632565000036": "Fiji Water",
  "632565000227": "Fiji Water", "632565000234": "Fiji Water", "632565000043": "Fiji Water",
  "786162002501": "Smart Water", "786162002518": "Smart Water", "786162002525": "Smart Water",
  "786162002594": "Smart Water", "786162375100": "Smart Water", "786162002600": "Smart Water",
  "896716001005": "Voss", "896716001012": "Voss", "896716001029": "Voss",
  "896716001036": "Voss", "896716002002": "Voss", "896716002019": "Voss",
  "068274540011": "Nestle Pure Life", "068274540028": "Nestle Pure Life", "068274540103": "Nestle Pure Life",
  "068274540219": "Nestle Pure Life", "068274348846": "Nestle Pure Life", "068274541018": "Nestle Pure Life",
  "071142000109": "Arrowhead", "071142000116": "Arrowhead", "071142000123": "Arrowhead",
  "071142000154": "Arrowhead", "071142006231": "Arrowhead", "071142006248": "Arrowhead",
  "654871100019": "Crystal Geyser", "654871100026": "Crystal Geyser", "654871100033": "Crystal Geyser",
  "654871100101": "Crystal Geyser", "654871100118": "Crystal Geyser", "654871100125": "Crystal Geyser",
  "082657802015": "Deer Park", "082657802022": "Deer Park", "082657802039": "Deer Park",
  "082657802305": "Deer Park", "082657802312": "Deer Park", "082657802329": "Deer Park",
  "083757802017": "Ice Mountain", "083757802024": "Ice Mountain", "083757802031": "Ice Mountain",
  "083757802208": "Ice Mountain", "083757802215": "Ice Mountain", "083757802222": "Ice Mountain",
  "068274102011": "Ozarka", "068274102028": "Ozarka", "068274102035": "Ozarka",
  "068274102301": "Ozarka", "068274102318": "Ozarka", "068274102325": "Ozarka",
  "073430000018": "Zephyrhills", "073430000025": "Zephyrhills", "073430000032": "Zephyrhills",
  "073430000308": "Zephyrhills", "073430000315": "Zephyrhills", "073430000322": "Zephyrhills",
  "851167000019": "Essentia", "851167000026": "Essentia", "851167000033": "Essentia",
  "851167000101": "Essentia", "851167000118": "Essentia", "851167000125": "Essentia",
  "851750000109": "Core", "851750000116": "Core", "851750000123": "Core",
  "851750000208": "Core", "851750000215": "Core", "851750000222": "Core",
  "012000172410": "Lifewtr", "012000172427": "Lifewtr", "012000172434": "Lifewtr",
  "012000172441": "Lifewtr", "012000172458": "Lifewtr", "012000172465": "Lifewtr",
  "052000135503": "Propel", "052000135510": "Propel", "052000135527": "Propel",
  "052000135602": "Propel", "052000135619": "Propel", "052000135626": "Propel",
  "184739000101": "Hint", "184739000118": "Hint", "184739000125": "Hint",
  "184739000132": "Hint", "184739000149": "Hint", "184739000156": "Hint",
  "012993101015": "La Croix", "012993101022": "La Croix", "012993101039": "La Croix",
  "012993101107": "La Croix", "012993101114": "La Croix", "012993101121": "La Croix",
  "074780000017": "Perrier", "074780000024": "Perrier", "074780000031": "Perrier",
  "074780000109": "Perrier", "074780000116": "Perrier", "074780000123": "Perrier",
  "041508800013": "San Pellegrino", "041508800020": "San Pellegrino", "041508800037": "San Pellegrino",
  "041508800105": "San Pellegrino", "041508800112": "San Pellegrino", "041508800129": "San Pellegrino",
  "07464400001": "Mountain Valley", "074644000028": "Mountain Valley", "074644000035": "Mountain Valley",
  "893147000014": "Icelandic Glacial", "893147000021": "Icelandic Glacial", "893147000038": "Icelandic Glacial",
};

// --- CITY WATER DATA ---------------------------------------------------------
const CITY_DATA = {
  "Austin, TX":{ utility:"Austin Water", source:"Colorado River / Barton Springs", tds:312, ph:7.8, hardness:"Moderate (142 mg/L)", contaminants:[
    {name:"Chromium-6",level:0.22,limit:0.10,unit:"ppb",risk:"high",category:"Heavy Metal",detail:"Known carcinogen. Exceeds CA health goal by 11x.",removed:true},
    {name:"PFAS (PFOA)",level:1.8,limit:0.004,unit:"ppt",risk:"high",category:"Forever Chemicals",detail:"Cancer, thyroid disruption, immune suppression. Never breaks down.",removed:true},
    {name:"Haloacetic Acids",level:42.3,limit:60,unit:"ppb",risk:"medium",category:"Disinfection Byproduct",detail:"Forms when chlorine reacts with organic matter. Carcinogen.",removed:true},
    {name:"Trihalomethanes",level:38.1,limit:80,unit:"ppb",risk:"medium",category:"Disinfection Byproduct",detail:"Cancer risk and reproductive harm.",removed:true},
    {name:"Nitrate",level:2.1,limit:10,unit:"ppm",risk:"low",category:"Agricultural Runoff",detail:"Dangerous for infants under 6 months.",removed:true},
    {name:"Microplastics",level:"Detected",limit:"None set",unit:"",risk:"medium",category:"Emerging Contaminant",detail:"Found in blood, lungs, placentas. No safe level.",removed:true},
  ]},
  "Chicago, IL":{ utility:"City of Chicago", source:"Lake Michigan", tds:220, ph:7.9, hardness:"Moderate (143 mg/L)", contaminants:[
    {name:"Lead",level:18.4,limit:15,unit:"ppb",risk:"high",category:"Heavy Metal",detail:"EXCEEDS LEGAL LIMIT. 400,000+ lead service lines, most of any US city.",removed:true},
    {name:"Chromium-6",level:0.26,limit:0.10,unit:"ppb",risk:"high",category:"Heavy Metal",detail:"Known carcinogen.",removed:true},
    {name:"PFAS (Total)",level:3.4,limit:0.004,unit:"ppt",risk:"high",category:"Forever Chemicals",detail:"Multiple PFAS compounds found.",removed:true},
    {name:"Haloacetic Acids",level:39.2,limit:60,unit:"ppb",risk:"medium",category:"Disinfection Byproduct",detail:"65% of legal limit.",removed:true},
    {name:"Microplastics",level:"Detected",limit:"None set",unit:"",risk:"medium",category:"Emerging Contaminant",detail:"Lake Michigan source. Microplastic hotspot.",removed:true},
  ]},
  "Los Angeles, CA":{ utility:"LADWP", source:"Colorado River / Northern CA / Groundwater", tds:486, ph:7.6, hardness:"Hard (218 mg/L)", contaminants:[
    {name:"Chromium-6",level:0.51,limit:0.10,unit:"ppb",risk:"high",category:"Heavy Metal",detail:"5x above CA health goal.",removed:true},
    {name:"PFAS (Total)",level:4.2,limit:0.004,unit:"ppt",risk:"high",category:"Forever Chemicals",detail:"Bioaccumulate in breast milk.",removed:true},
    {name:"Arsenic",level:2.8,limit:10,unit:"ppb",risk:"medium",category:"Heavy Metal",detail:"Skin, bladder, lung cancer.",removed:true},
    {name:"Haloacetic Acids",level:51.3,limit:60,unit:"ppb",risk:"medium",category:"Disinfection Byproduct",detail:"Near legal limit.",removed:true},
    {name:"Microplastics",level:"Detected",limit:"None set",unit:"",risk:"medium",category:"Emerging Contaminant",detail:"94% of LA samples positive.",removed:true},
  ]},
  "New York, NY":{ utility:"NYC DEP", source:"Catskill/Delaware Watersheds", tds:144, ph:7.2, hardness:"Soft (31 mg/L)", contaminants:[
    {name:"Lead",level:8.1,limit:15,unit:"ppb",risk:"medium",category:"Heavy Metal",detail:"Lead service lines in pre-1986 buildings.",removed:true},
    {name:"PFAS (PFOA)",level:1.1,limit:0.004,unit:"ppt",risk:"high",category:"Forever Chemicals",detail:"No safe level for children.",removed:true},
    {name:"Microplastics",level:"Detected",limit:"None set",unit:"",risk:"medium",category:"Emerging Contaminant",detail:"Found in all NYC samples.",removed:true},
  ]},
  "Denver, CO":{ utility:"Denver Water", source:"South Platte River", tds:198, ph:8.1, hardness:"Soft (72 mg/L)", contaminants:[
    {name:"Chromium-6",level:0.18,limit:0.10,unit:"ppb",risk:"high",category:"Heavy Metal",detail:"Known carcinogen.",removed:true},
    {name:"PFAS (PFOS)",level:2.1,limit:0.004,unit:"ppt",risk:"high",category:"Forever Chemicals",detail:"Liver damage, cancer risk.",removed:true},
    {name:"Lead",level:3.2,limit:15,unit:"ppb",risk:"medium",category:"Heavy Metal",detail:"No safe level.",removed:true},
    {name:"Microplastics",level:"Detected",limit:"None set",unit:"",risk:"medium",category:"Emerging Contaminant",detail:"Found in human blood.",removed:true},
  ]},
  "Houston, TX":{ utility:"Houston Water", source:"Trinity River / Lake Houston", tds:388, ph:7.4, hardness:"Hard (182 mg/L)", contaminants:[
    {name:"PFAS (PFOS)",level:5.8,limit:0.004,unit:"ppt",risk:"high",category:"Forever Chemicals",detail:"1,450x EPA health advisory level.",removed:true},
    {name:"Chromium-6",level:0.31,limit:0.10,unit:"ppb",risk:"high",category:"Heavy Metal",detail:"Exceeds health goal.",removed:true},
    {name:"Arsenic",level:3.2,limit:10,unit:"ppb",risk:"medium",category:"Heavy Metal",detail:"8x EWG guideline.",removed:true},
    {name:"Microplastics",level:"Detected",limit:"None set",unit:"",risk:"medium",category:"Emerging Contaminant",detail:"Industrial river source.",removed:true},
  ]},
  "Phoenix, AZ":{ utility:"Phoenix Water Services", source:"Colorado River / Salt River", tds:620, ph:7.9, hardness:"Very Hard (280 mg/L)", contaminants:[
    {name:"Chromium-6",level:0.44,limit:0.10,unit:"ppb",risk:"high",category:"Heavy Metal",detail:"Colorado River source.",removed:true},
    {name:"PFAS (Total)",level:3.1,limit:0.004,unit:"ppt",risk:"high",category:"Forever Chemicals",detail:"Military base contamination.",removed:true},
    {name:"Arsenic",level:4.8,limit:10,unit:"ppb",risk:"medium",category:"Heavy Metal",detail:"Arizona geology naturally high.",removed:true},
    {name:"Microplastics",level:"Detected",limit:"None set",unit:"",risk:"medium",category:"Emerging Contaminant",detail:"Detected in all AZ samples.",removed:true},
  ]},
};

// --- ZIP CODE MAPPING --------------------------------------------------------
const ZIP_MAP = {
  "78701":"Austin, TX","78702":"Austin, TX","78703":"Austin, TX",
  "60601":"Chicago, IL","60602":"Chicago, IL","60603":"Chicago, IL","60604":"Chicago, IL","60605":"Chicago, IL","60606":"Chicago, IL",
  "90001":"Los Angeles, CA","90210":"Los Angeles, CA","90024":"Los Angeles, CA",
  "10001":"New York, NY","10002":"New York, NY","10003":"New York, NY",
  "80201":"Denver, CO","80202":"Denver, CO","80203":"Denver, CO",
  "77001":"Houston, TX","77002":"Houston, TX","77003":"Houston, TX",
  "85001":"Phoenix, AZ","85002":"Phoenix, AZ","85003":"Phoenix, AZ"
};

// --- UTILITY FUNCTIONS -------------------------------------------------------
function getRiskScore(c){
  if(!c)return 0;
  return Math.min(100,c.filter(x=>x.risk==="high").length*22+c.filter(x=>x.risk==="medium").length*11+10);
}

// Prefer the backend's authoritative water_threat_score when present (it uses
// the bounded server-side formula), and fall back to the local getRiskScore
// only for legacy CITY_DATA paths where data.riskScore is not set.
// Using ?? (not ||) so a legitimate low score of 0 is not overridden.
function getDisplayScore(d) {
  if (!d) return 0;
  return d.riskScore ?? getRiskScore(d.contaminants);
}

function generateSessionId() {
  const id = 'wtr_' + Math.random().toString(36).slice(2, 11) + '_' + Date.now().toString(36);
  try { sessionStorage.setItem('wtr_session_id', id); } catch(e) {}
  return id;
}

function getSessionId() {
  try { return sessionStorage.getItem('wtr_session_id') || generateSessionId(); } catch(e) { return generateSessionId(); }
}

function trackEvent(eventName, properties = {}) {
  const base = { session_id: getSessionId(), app_version: '2.0', timestamp: new Date().toISOString() };
  try { window.posthog?.capture(eventName, { ...base, ...properties }); } catch(e) {}
}

// Auto-detect input type from a freeform search string.
// 5-digit number -> zip. Contains a comma or matches a known city -> city. Else -> address.
function detectInputMode(raw) {
  const s = (raw || '').trim();
  if (!s) return 'address';
  if (/^\d{5}$/.test(s)) return 'zip';
  if (s.includes(',')) return 'city';
  const lower = s.toLowerCase();
  for (const k of Object.keys(CITY_DATA)) {
    const cityName = k.split(',')[0].toLowerCase().trim();
    if (lower === cityName || lower.startsWith(cityName + ' ')) return 'city';
  }
  return 'address';
}

const API_BASE = 'https://generosity-sales-engine-mvp-api.onrender.com';
const API_BEARER = 'Bearer ' + SERVICE_TOKEN;

const ARROW = '\u2192';
const TRADEMARK = '\u2122';

// --- MAIN APP COMPONENT ------------------------------------------------------
export default function TrustButVerify(){
  const [tab,setTab]=useState("tbv");
  const [phase,setPhase]=useState("landing");
  const [inputMode,setInputMode]=useState("address");
  const [input,setInput]=useState("");
  const [data,setData]=useState(null);
  const [scanStep,setScanStep]=useState(0);
  const [email,setEmail]=useState("");
  const [submitted,setSubmitted]=useState(false);
  const [showHub,setShowHub]=useState(false);
  const [animating,setAnimating]=useState(false);
  const [gaugeOn,setGaugeOn]=useState(false);
  const [insightView,setInsightView]=useState("report");
  const [tbvView,setTbvView]=useState("home");
  // Source-tag to Bottle-Scan handoff: set by WTRBottleScreen's Trust But
  // Verify bridge, consumed once by BottleScanView on mount.
  const [scanHandoff,setScanHandoff]=useState(null);
  const [showProfile,setShowProfile]=useState(false);
  const [showIntake,setShowIntake]=useState(false);
  const [showDevices,setShowDevices]=useState(false);
  const [showHealth,setShowHealth]=useState(false);
  const [showReports,setShowReports]=useState(false);
  const [showHelp,setShowHelp]=useState(false);
  const [showShop,setShowShop]=useState(false);
  const [showAuth,setShowAuth]=useState(false);
  const [showLowContaminants,setShowLowContaminants]=useState(false);
  const [authFlash,setAuthFlash]=useState("");
  const [drawerOpen,setDrawerOpen]=useState(false);
  // WTR+ tier — server truth from GET /api/wtr/me/entitlements (Commit 5).
  // Replaces founder-PIN client gating: the PIN, its modal, the long-press,
  // and the founder localStorage flag are all deleted. Tier is 'free' until
  // the server says 'plus'; lib/entitlements fires 'wtr-tier-changed' on
  // every refresh. Display truth only — every paid endpoint is enforced
  // server-side with 403 WTR_PLUS_REQUIRED.
  const [tier,setTier]=useState(currentTier());
  const isPlus = tier==='plus';
  const [showPaywall,setShowPaywall]=useState(false);
  useEffect(()=>{
    try{localStorage.removeItem('wtr_founder_mode');}catch(e){}
    const onTier=(e)=>setTier(e&&e.detail&&e.detail.tier==='plus'?'plus':'free');
    window.addEventListener('wtr-tier-changed',onTier);
    refreshTier();
    return ()=>window.removeEventListener('wtr-tier-changed',onTier);
  },[]);

  // Tier guard: never leave a user on a tab their tier does not own.
  useEffect(()=>{
    if(!isPlus&&(tab==='my-hydration'||tab==='wtr-btl'||tab==='wtr-hub')) setTab('tbv');
    if(isPlus&&tab==='my-intake') setTab('my-hydration');
  },[isPlus,tab]);

  const [inputError, setInputError] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [engagementPhase, setEngagementPhase] = useState("idle");
  const [householdProfile, setHouseholdProfile] = useState({ has_children: null, is_pregnant: null, has_filter: null });
  // eslint-disable-next-line no-unused-vars
  const [pushResult, setPushResult] = useState(null);
  const [capturedProspectId, setCapturedProspectId] = useState(null);

  // v3.1 proximity fallback: when a ZIP has no direct utility mapping, the backend
  // returns status:'select_utility' with up to 4 nearby candidates. utilitySelect
  // holds {zip, user_location, candidates}. selectingPwsid tracks the card the user
  // tapped (for per-card loading). selectError surfaces a pwsid-resolve failure
  // inline without leaving the picker.
  const [utilitySelect, setUtilitySelect] = useState(null);
  const [selectingPwsid, setSelectingPwsid] = useState(null);
  const [selectError, setSelectError] = useState("");
  // Which candidate's report is currently on screen. Drives the inline switcher so
  // the active utility is excluded from the "other utilities" list.
  const [activePwsid, setActivePwsid] = useState(null);

  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportOptInStatus, setReportOptInStatus] = useState(() => { try { return localStorage.getItem('wtr_report_status') || 'none'; } catch(e) { return 'none'; } });
  const [reportOptInEmail, setReportOptInEmail] = useState(() => { try { return localStorage.getItem('wtr_report_email') || ''; } catch(e) { return ''; } });
  const [reportModalScreen, setReportModalScreen] = useState('form');
  const [reportEmail, setReportEmail] = useState('');
  const [reportZip, setReportZip] = useState('');
  const [reportEmailError, setReportEmailError] = useState('');
  const [reportZipError, setReportZipError] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSubmitError, setReportSubmitError] = useState('');

  const inputRef=useRef(null);

 useEffect(() => { installAuthGuard(); registerServiceWorker(); }, []);

  // Phase 3: when any identity call returns 401 (no session, or expired), the auth
  // guard in lib/auth fires 'wtr-auth-expired'. Open the sign-in screen and surface
  // a brief notice, so an expired session is one clean re-sign-in rather than a
  // screen full of empty or broken data.
  useEffect(() => {
    const onExpired = () => {
      refreshTier();
      setShowAuth(true);
      setAuthFlash("Your session expired. Please sign in to continue.");
      setTimeout(() => setAuthFlash(""), 5000);
    };
    window.addEventListener('wtr-auth-expired', onExpired);
    return () => window.removeEventListener('wtr-auth-expired', onExpired);
  }, []);

  // Auth Phase 1: if the magic-link brought us here with ?auth_token, finish
  // sign-in, store the session, and surface a brief confirmation.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await consumeAuthTokenFromUrl();
      if (!res || cancelled) return;
      if (res.email) {
        refreshTier();
        setAuthFlash(`Signed in as ${res.email}`);
        setTimeout(() => { if (!cancelled) setAuthFlash(""); }, 4000);
      } else if (res.error) {
        setAuthFlash(res.error);
        setTimeout(() => { if (!cancelled) setAuthFlash(""); }, 5000);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // SHARED REPORTS (2026-10-01): a /report link may name the utility the
  // sender was viewing. The ZIP scan runs exactly as before; once a report is
  // on screen, the effect below swaps to the named utility if it is not the
  // one that landed. Stored in a ref so the swap survives the scan's state
  // churn and fires exactly once per link.
  const pendingPwsidRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const { tab: targetTab, scan, pwsid } = e.detail || {};
      if (targetTab) setTab(targetTab);
      pendingPwsidRef.current = pwsid || null;
      if (scan) { setInput(scan); setInputMode('zip'); setTimeout(() => startScan(scan), 200); }
    };
    window.addEventListener('wtr-navigate', handler);
    return () => window.removeEventListener('wtr-navigate', handler);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const want = pendingPwsidRef.current;
    if (!want || phase !== "results" || selectingPwsid) return;
    pendingPwsidRef.current = null;
    if (activePwsid !== want) resolveUtility(want);
    // eslint-disable-next-line
  }, [phase, activePwsid, selectingPwsid]);

  const REPORT_CONSENT_TEXT = 'By submitting your email, you agree to receive a free Monthly Water Intelligence Report from Generosity Water based on your zip code. Your report is generated by the Generosity Water Intelligence Engine (WIQ) and includes local water quality data, contaminant alerts, and relevant news for your area. You can unsubscribe at any time by clicking the unsubscribe link in any email. We do not sell your email address. View our Privacy Policy at generositywater.com/privacy.';
  const REPORT_CONSENT_VERSION = 'v1.0.0-2026-04-08';

  useEffect(() => {
    const storedEmail = reportOptInEmail;
    const storedZip = data?.zip || '';
    if (storedEmail && storedZip) {
      fetch(`${API_BASE}/api/water-report/status?email=${encodeURIComponent(storedEmail)}&zip=${encodeURIComponent(storedZip)}`, { headers: { 'Authorization': API_BEARER } })
        .then(r => r.json())
        .then(d => {
          if (d.status && d.status !== reportOptInStatus) {
            setReportOptInStatus(d.status);
            try { localStorage.setItem('wtr_report_status', d.status); } catch(e) {}
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line
  }, [data?.zip]);

  function handleReportSubscribe() {
    let hasErr = false;
    if (!reportEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reportEmail)) { setReportEmailError('Please enter a valid email address'); hasErr = true; } else { setReportEmailError(''); }
    const cleanZip = reportZip.replace(/\D/g, '').slice(0, 5);
    if (!cleanZip || !/^\d{5}$/.test(cleanZip)) { setReportZipError('Please enter a 5-digit ZIP code'); hasErr = true; } else { setReportZipError(''); }
    if (hasErr) return;
    setReportSubmitting(true); setReportSubmitError('');
    fetch(`${API_BASE}/api/water-report/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': API_BEARER },
      body: JSON.stringify({
        email: reportEmail.trim().toLowerCase(),
        zip: cleanZip,
        consent_text: REPORT_CONSENT_TEXT,
        consent_text_version: REPORT_CONSENT_VERSION,
        source: 'wtr_app_intel_tab',
        session_id: getSessionId()
      })
    })
      .then(r => r.json())
      .then(d => {
        setReportSubmitting(false);
        if (d.error) { setReportSubmitError(d.error); return; }
        setReportOptInStatus('pending');
        setReportOptInEmail(reportEmail.trim().toLowerCase());
        try {
          localStorage.setItem('wtr_report_status', 'pending');
          localStorage.setItem('wtr_report_email', reportEmail.trim().toLowerCase());
        } catch(e) {}
        setReportModalScreen('success');
        trackEvent('water_report_subscribed', { zip: cleanZip, email: reportEmail.trim().toLowerCase() });
      })
      .catch(() => {
        setReportSubmitting(false);
        setReportSubmitError('Something went wrong. Please try again.');
      });
  }

  function openReportModal() {
    setReportModalScreen('form');
    setReportEmail(reportOptInEmail || email || '');
    setReportZip(data?.zip || '');
    setReportEmailError('');
    setReportZipError('');
    setReportSubmitError('');
    setReportSubmitting(false);
    setReportModalOpen(true);
    trackEvent('water_report_modal_opened');
  }

  const SCAN_STEPS = [
    "Locating water utility...",
    "Querying EPA SDWIS database...",
    "Cross-referencing EWG Tap Water database...",
    "Analyzing contaminant levels...",
    "Comparing to health guidelines...",
    "Generating your intelligence report..."
  ];

  function resolveCity(raw){
    const s=raw.trim().toLowerCase();
    if(/^\d{5}$/.test(s)) return ZIP_MAP[s]||null;
    return Object.keys(CITY_DATA).find(k=>{ const cityName = k.split(",")[0].toLowerCase().trim(); return s.includes(cityName); })||null;
  }

  function startScan(directInput){
    const scanInput = (typeof directInput === 'string') ? directInput : input;
    setInputError("");
    if(!scanInput || !scanInput.trim()){
      setInputError("Enter your address, ZIP, or city to scan");
      inputRef.current?.classList.add('input-error');
      setTimeout(() => inputRef.current?.classList.remove('input-error'), 400);
      return;
    }
    if(isScanning) return;

    // Auto-detect input mode from the raw input so downstream views and capture payload still work
    const detectedMode = detectInputMode(scanInput);
    if (detectedMode !== inputMode) setInputMode(detectedMode);

    setIsScanning(true);
    const cleanInput = scanInput.replace(/<[^>]*>/g, '').trim().slice(0, 100);
    trackEvent('scan_started', { input_mode: detectedMode, input_length: cleanInput.length });
    setPhase("scanning"); setScanStep(0);
    // Reset proximity-fallback state so a stale candidate list from a prior scan
    // never leaks into a new (possibly direct-mapped) report.
    setUtilitySelect(null); setActivePwsid(null); setSelectError("");
    let step=0;
    const t=setInterval(()=>{
      step++; setScanStep(step);
      if(step>=SCAN_STEPS.length){
        clearInterval(t);
        setTimeout(async()=>{
          const city=resolveCity(cleanInput);
          if(city){
            const raw=CITY_DATA[city];
            setData({...raw,city:city});
            setPhase("results"); setTab("wtr-intel");
            trackEvent('scan_completed', { city, risk_score: getRiskScore(raw.contaminants), contaminant_count: raw.contaminants?.length, source:'local' });
            setTimeout(()=>{setShowHub(true);setGaugeOn(true);},300);
            setTimeout(()=>setAnimating(true),700);
            setIsScanning(false); return;
          }
          const zipMatch = cleanInput.match(/\d{5}/);
          if(zipMatch){
            try {
              const oracleRes = await fetch(`${API_BASE}/api/wtr/report?zip=${zipMatch[0]}`, { headers: { 'Authorization': API_BEARER } });
              if(oracleRes.ok){
                const report = await oracleRes.json();
                // v3.1: ZIP has no direct utility mapping but proximity found candidates.
                // Must run BEFORE the direct-report gate below, because a select_utility
                // payload has no contaminants and would otherwise fall through to not_found.
                if(report && report.status === 'select_utility' && (report.candidates?.length || 0) > 0){
                  const cands = report.candidates;
                  const sorted = [...cands].sort((a, b) => (b.population_served || 0) - (a.population_served || 0));
                  setUtilitySelect({ zip: report.zip || zipMatch[0], user_location: report.user_location || null, candidates: cands });
                  setSelectError("");
                  setActivePwsid(null);
                  setTbvView("home");
                  trackEvent('utility_candidates_shown', { zip: report.zip || zipMatch[0], candidate_count: cands.length });
                  // Auto-load the most-likely utility (largest population) and render its
                  // full report immediately. The remaining candidates render inline inside
                  // the report as a switcher (EWG pattern). Loader stays up via isScanning
                  // until resolveUtility settles. No setIsScanning(false) / no early phase
                  // change here, so a failed auto-load can fall back to the manual picker.
                  resolveUtility(sorted[0].pwsid);
                  return;
                }
                if(report && report.utility && report.status !== 'not_found' && (report.contaminants?.length || 0) > 0){
                  const cityLabel = `${report.utility} | ${zipMatch[0]}`;
                  const transformed = transformOracleReport(report, cityLabel, zipMatch[0]);
                  setData(transformed);
                  setPhase("results"); setTab("wtr-intel");
                  trackEvent('scan_completed', { city: cityLabel, zip: zipMatch[0], risk_score: getDisplayScore(transformed), contaminant_count: transformed.contaminants?.length, source:'wtr-oracle' });
                  setTimeout(()=>{setShowHub(true);setGaugeOn(true);},300);
                  setTimeout(()=>setAnimating(true),700);
                  setIsScanning(false); return;
                }
              }
            } catch(e){ console.warn('[WTR-ORACLE] Lookup failed:', e.message); }
          }
          trackEvent('scan_location_not_found', { input: cleanInput.slice(0,20) });
          setPhase("not_found"); setIsScanning(false);
        },300);
      }
    },480);
  }

  // v3.1: load a utility's full report by PWSID. Called two ways:
  //   1. Auto, on a select_utility result, for the most-likely candidate (initial load).
  //   2. By tap, from the inline switcher, to swap the report to another utility.
  // utilitySelect (the candidate list) is intentionally kept alive on success so the
  // inline switcher persists. activePwsid tracks which utility is on screen.
  async function resolveUtility(pwsid){
    if(!pwsid || selectingPwsid) return;
    const hadReport = phase === "results"; // true => user is swapping; false => initial auto-load
    const zip = utilitySelect?.zip || (input.match(/\d{5}/)?.[0]) || null;
    setSelectingPwsid(pwsid);
    setSelectError("");
    trackEvent('utility_candidate_selected', { pwsid, zip });
    try {
      const res = await fetch(`${API_BASE}/api/wtr/report?pwsid=${encodeURIComponent(pwsid)}`, { headers: { 'Authorization': API_BEARER } });
      if(!res.ok) throw new Error(`HTTP ${res.status}`);
      const report = await res.json();
      // Defensive: require a usable report (utility + at least one contaminant) before
      // committing. An empty/invalid body must not crash or blank the screen.
      if(report && report.utility && report.status !== 'not_found' && (report.contaminants?.length || 0) > 0){
        const cityLabel = `${report.utility} | ${zip || report.zip || ''}`.trim().replace(/\|\s*$/, '').trim();
        const transformed = transformOracleReport(report, cityLabel, zip || report.zip || '');
        setData(transformed);
        setActivePwsid(pwsid);
        setSelectingPwsid(null);
        setIsScanning(false);
        setPhase("results"); setTab("wtr-intel"); setTbvView("home");
        trackEvent('scan_completed', { city: cityLabel, zip, pwsid, risk_score: getDisplayScore(transformed), contaminant_count: transformed.contaminants?.length, source: hadReport ? 'wtr-oracle-pwsid-swap' : 'wtr-oracle-pwsid' });
        // Re-run the gauge sweep so swapping utilities animates the new score.
        setShowHub(false); setGaugeOn(false); setAnimating(false);
        setTimeout(()=>{setShowHub(true);setGaugeOn(true);},300);
        setTimeout(()=>setAnimating(true),700);
        return;
      }
      // Empty / invalid report.
      setSelectingPwsid(null);
      setIsScanning(false);
      setSelectError(hadReport
        ? "That utility did not return a report. Try another from the list."
        : "That utility did not return a report. Pick another below, or search a different location.");
      if(!hadReport){ setActivePwsid(null); setPhase("select_utility"); setTbvView("home"); }
      trackEvent('utility_resolve_failed', { pwsid, zip, reason: 'empty_or_invalid_report', stage: hadReport ? 'swap' : 'initial' });
    } catch(e){
      setSelectingPwsid(null);
      setIsScanning(false);
      setSelectError("Could not load that utility. Check your connection and try again.");
      if(!hadReport){ setActivePwsid(null); setPhase("select_utility"); setTbvView("home"); }
      trackEvent('utility_resolve_failed', { pwsid, zip, reason: e.message, stage: hadReport ? 'swap' : 'initial' });
    }
  }

  async function handleEmailSubmit(scanPhase) {
    const emailTrimmed = email.trim().toLowerCase();
    if (!emailTrimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) { setEmailError("Please enter a valid email address"); return; }
    setSubmitted(true);
    trackEvent('email_captured', { city: data?.city, risk_score: getDisplayScore(data), scan_phase: scanPhase });
    setTimeout(() => setEngagementPhase("push_prompt"), 2000);
    try {
      const payload = {
        email: emailTrimmed,
        zip: input && /^\d{5}$/.test(input.trim()) ? input.trim() : null,
        city: data?.city || input || null,
        risk_score: getDisplayScore(data),
        high_concern_contaminants: data?.contaminants?.filter(c => c.risk === 'high').map(c => c.name) || [],
        source: 'wtr_app',
        session_id: getSessionId(),
        address: inputMode === 'address' ? input : null,
        scan_phase: scanPhase
      };
      const resp = await fetch(`${API_BASE}/api/wtr/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': API_BEARER },
        body: JSON.stringify(payload)
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();
      if (result.prospect_id) setCapturedProspectId(result.prospect_id);
    } catch (err) {
      try {
        const dlq = JSON.parse(localStorage.getItem('wtr_capture_dlq') || '[]');
        dlq.push({ email: emailTrimmed, city: data?.city || input, ts: Date.now(), scan_phase: scanPhase });
        localStorage.setItem('wtr_capture_dlq', JSON.stringify(dlq.slice(-20)));
      } catch (e) {}
      console.warn('[WTR Capture] Failed, queued for retry:', err.message);
    }
  }

  // Render-time score: prefer the backend's authoritative water_threat_score
  // (set into data.riskScore by transformOracleReport), fall back to local calc
  // only for legacy CITY_DATA paths. This is what drives the header pill,
  // the gauge, and the bottom-nav badge.
  const riskScore = getDisplayScore(data);
  const highRisk = data?.contaminants.filter(c=>c.risk==="high")||[];
  // Split the contaminant list so the long green/low tail collapses behind one
  // toggle instead of forcing the user to scroll past every low detection.
  const shownContaminants = data?.contaminants.filter(c => c.risk !== 'low') || [];
  const lowContaminants = data?.contaminants.filter(c => c.risk === 'low') || [];
  const cityShort = data?.city?.split(",")[0] || '';

  // Free = 3 tabs, TBV landing, My Intake center. Plus = 5 tabs, My
  // Hydration center hero. "Absorption, not just intake": the free tier is
  // literally just intake; the paid tier is absorption intelligence.
  const navTabs = isPlus
    ? [
        { id:"tbv", label:`Trust but Verify${TRADEMARK}` },
        { id:"wtr-intel", label:"WTR INTEL", badge: data ? riskScore : null },
        { id:"my-hydration", label:"My Hydration", center:true },
        { id:"wtr-btl", label:"WTR BTL" },
        { id:"wtr-hub", label:"WTR HUB" },
      ]
    : [
        { id:"tbv", label:`Trust but Verify${TRADEMARK}` },
        { id:"my-intake", label:"My Intake", center:true },
        { id:"wtr-intel", label:"WTR INTEL", badge: data ? riskScore : null },
      ];

  const headerRiskTone = riskScore > 66 ? '#B84A4A' : riskScore > 33 ? '#C89B3C' : '#4A8A6F';
  const headerRiskBg   = riskScore > 66 ? 'rgba(184, 74, 74, 0.10)' : riskScore > 33 ? 'rgba(200, 155, 60, 0.10)' : 'rgba(74, 138, 111, 0.10)';

  const shopifyHubUrl = `https://generositywtr.myshopify.com/products/home-hydration-hub?utm_source=wtr-app&utm_medium=in-app-report&utm_campaign=water-threat-scan&utm_content=${encodeURIComponent((data?.city||'direct').replace(/\s/g,'-').toLowerCase())}&utm_term=${riskScore}&discount=WELCOME100`;

  // v3.1: Hide header on Google-homepage landing screen
  const isGoogleLanding = tab === "tbv" && tbvView === "home" && phase === "landing";
  const showHeader = !isGoogleLanding && tab !== "wtr-btl" && tab !== "wtr-hub" && tab !== "my-hydration" && tab !== "my-intake";

  return (
    <div
      className="min-h-screen mx-auto relative flex flex-col"
      style={{ maxWidth: 480, fontFamily: 'Montserrat, sans-serif', background: 'transparent' }}
      data-testid="trust-but-verify-app"
    >
      <style>{`
        @keyframes pulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.08);opacity:0.8}}
        @keyframes slideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes ripple{0%{transform:scale(0.9);opacity:0.8}100%{transform:scale(2.2);opacity:0}}
        @keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}
        @keyframes shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-6px)}40%,80%{transform:translateX(6px)}}
        .input-error{animation:shake 0.35s ease;border-color:#B84A4A!important}
        .tbv-card{animation:slideUp 0.4s ease forwards}
      `}</style>

      {showHeader && (
        <AppHeader
          variant="primary"
          title={tab === "wtr-intel" ? "WTR Intel" : "Trust but Verify"}
          onMenu={() => setDrawerOpen(true)}
          pill={data ? { label: `Score ${riskScore}`, tone: "score" } : null}
        />
      )}

      <div className="flex-1 overflow-y-auto" style={{ paddingBottom: 80 }}>
        {tab === "tbv" && (
          <div data-testid="tbv-tab">
            {/* Top-level Home Water / Bottle Scan toggle.
                Visible on scan view, results, scanning, not_found, anywhere except the
                Google-homepage landing screen (where it lives inline below the search bar). */}
            {!isGoogleLanding && (
              <div className="sticky top-0 z-[50]" style={{ padding: '14px 16px 10px', background: '#FFFFFF', display: 'flex', justifyContent: 'center' }}>
                {/* One toggle language app-wide: small, centered, icon pills (matches landing + INTEL) */}
                <div style={{ width: '100%', maxWidth: 280 }}>
                  <SegmentedToggle
                    testId="tbv-toggle"
                    size="small"
                    value={tbvView}
                    onChange={setTbvView}
                    options={[
                      { id: "home", label: "Home Water", icon: <Icon name="home" size={10} color={tbvView === "home" ? "#51B0E6" : "#A6A8AB"} /> },
                      { id: "scan", label: "Bottle Scan", icon: <Icon name="scan" size={10} color={tbvView === "scan" ? "#51B0E6" : "#A6A8AB"} /> },
                    ]}
                  />
                </div>
              </div>
            )}

            {tbvView === "home" && phase === "landing" && (
              <div
                data-testid="home-landing"
                style={{
                  minHeight: 'calc(100vh - 80px)',
                  minHeight: 'calc(100dvh - 80px)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '32px 20px 24px',
                }}
              >
                {/* v3.2: Google-homepage logo treatment (80px, matches Google mobile) */}
                <div
                  style={{ marginTop: 48, marginBottom: 32, userSelect: 'none' }}
                >
                  <img
                    src="/generosity-logo.png"
                    alt="Generosity Water Intelligence"
                    style={{ height: 80, width: 'auto', display: 'block' }}
                  />
                </div>

                {/* v3.3: Top group - logo, search, EPA, mode toggle (Home Water / Bottle Scan) */}
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {/* Search bar with magnifying glass inside */}
                  <div style={{ width: '100%', maxWidth: 380, marginBottom: 8 }}>
                    <div
                      className="card-default"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 6px 0 16px',
                        minHeight: 52,
                        position: 'relative',
                      }}
                    >
                      <input
                        ref={inputRef}
                        data-testid="address-input"
                        type="text"
                        value={input}
                        onChange={(e) => { setInput(e.target.value); if (inputError) setInputError(""); }}
                        onKeyDown={(e) => { if (e.key === "Enter") startScan(); }}
                        placeholder="Enter your address, zip, or city"
                        style={{
                          flex: 1,
                          background: 'transparent',
                          border: 'none',
                          outline: 'none',
                          fontSize: 14,
                          fontFamily: 'Montserrat, sans-serif',
                          color: '#0F1419',
                          padding: '14px 0',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => startScan()}
                        data-testid="scan-btn"
                        aria-label="Search water intelligence"
                        className="cursor-pointer flex items-center justify-center"
                        style={{
                          width: 40,
                          height: 40,
                          background: 'transparent',
                          border: 'none',
                          borderRadius: 999,
                          marginLeft: 4,
                        }}
                      >
                        <Icon name="scan" size={22} color="#51B0E6" />
                      </button>
                    </div>
                    {inputError && (
                      <div className="text-caption" style={{ color: '#B84A4A', marginTop: 6, paddingLeft: 16, fontSize: 11 }}>
                        {inputError}
                      </div>
                    )}
                  </div>

                  {/* EPA caption */}
                  <div style={{ fontSize: 9, marginBottom: 14, color: '#A6A8AB', letterSpacing: '0.02em' }}>
                    EPA SDWIS + UCMR 5 Report
                  </div>

                  {/* Mode toggle: Home Water / Bottle Scan
                      Replaces the prior Address/ZIP/City toggle. Input mode is auto-detected on scan. */}
                  <div style={{ width: '100%', maxWidth: 280, marginBottom: 28 }}>
                    <SegmentedToggle
                      testId="mode-toggle-landing"
                      size="small"
                      value={tbvView}
                      onChange={(v) => {
                        setTbvView(v);
                        trackEvent('landing_mode_switched', { mode: v });
                      }}
                      options={[
                        { id: "home", label: "Home Water", icon: <Icon name="home" size={10} color={tbvView === "home" ? "#51B0E6" : "#A6A8AB"} /> },
                        { id: "scan", label: "Bottle Scan", icon: <Icon name="scan" size={10} color={tbvView === "scan" ? "#51B0E6" : "#A6A8AB"} /> },
                      ]}
                    />
                  </div>

                  {/* v3.3: Headline carries the punch. Paragraph supports it. */}
                  <div className="text-center" style={{ marginBottom: 32, maxWidth: 340, padding: '0 8px' }}>
                    <h1
                      className="font-display font-semibold"
                      style={{ fontSize: 28, lineHeight: 1.15, marginBottom: 12, letterSpacing: '-0.02em', color: '#0F1419' }}
                    >
                      Drink <span style={{ color: '#51B0E6' }}>Intelligently</span>.
                    </h1>
                    <p style={{ fontSize: 14, lineHeight: 1.55, color: '#3D4043' }}>
                      94% of US tap water contains microplastics. Yours probably does too. Find out what else is in your Water.
                    </p>
                  </div>
                </div>

                {/* v3.3: Spacer pushes Popular Cities toward bottom */}
                <div style={{ flex: 1, minHeight: 24 }} />

                {/* v3.3: Popular cities pinned near bottom - 3 on top row + 4 on bottom row */}
                <div className="text-center" style={{ width: '100%', maxWidth: 380, paddingBottom: 8 }}>
                  <div className="text-micro uppercase tracking-widest font-semibold" style={{ marginBottom: 12, color: '#A6A8AB', fontSize: 9, letterSpacing: '0.10em' }}>
                    POPULAR CITIES
                  </div>
                  {/* Top row: 3 cities */}
                  <div className="flex gap-1.5 justify-center" style={{ marginBottom: 8 }} data-testid="popular-cities-row1">
                    {Object.keys(CITY_DATA).slice(0, 3).map((city) => (
                      <button
                        key={city}
                        type="button"
                        onClick={() => { setInput(city); setInputMode("city"); startScan(city); }}
                        data-testid={`city-btn-${city.toLowerCase().replace(/[,\s]+/g, '-')}`}
                        className="font-medium rounded-pill cursor-pointer"
                        style={{
                          padding: '6px 14px',
                          fontSize: 11,
                          background: 'rgba(255, 255, 255, 0.45)',
                          color: '#1F6FA0',
                          border: '1px solid rgba(81, 176, 230, 0.22)',
                          backdropFilter: 'blur(30px) saturate(180%)',
                          WebkitBackdropFilter: 'blur(30px) saturate(180%)',
                        }}
                      >
                        {city.split(',')[0]}
                      </button>
                    ))}
                  </div>
                  {/* Bottom row: 4 cities */}
                  <div className="flex gap-1.5 justify-center" data-testid="popular-cities-row2">
                    {Object.keys(CITY_DATA).slice(3, 7).map((city) => (
                      <button
                        key={city}
                        type="button"
                        onClick={() => { setInput(city); setInputMode("city"); startScan(city); }}
                        data-testid={`city-btn-${city.toLowerCase().replace(/[,\s]+/g, '-')}`}
                        className="font-medium rounded-pill cursor-pointer"
                        style={{
                          padding: '6px 14px',
                          fontSize: 11,
                          background: 'rgba(255, 255, 255, 0.45)',
                          color: '#1F6FA0',
                          border: '1px solid rgba(81, 176, 230, 0.22)',
                          backdropFilter: 'blur(30px) saturate(180%)',
                          WebkitBackdropFilter: 'blur(30px) saturate(180%)',
                        }}
                      >
                        {city.split(',')[0]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {tbvView === "home" && phase === "scanning" && (
              <div className="mx-auto" style={{ maxWidth: 360, margin: '60px auto', padding: '0 20px', textAlign: 'center' }} data-testid="home-scanning">
                <div className="relative mx-auto" style={{ width: 80, height: 80, marginBottom: 22 }}>
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: 'rgba(81, 176, 230, 0.12)',
                      border: '2px solid rgba(81, 176, 230, 0.30)',
                      animation: 'ripple 1.4s ease-out infinite',
                    }}
                  />
                  <div
                    className="rounded-full flex items-center justify-center relative z-[1]"
                    style={{
                      width: 80,
                      height: 80,
                      background: 'linear-gradient(135deg, #51B0E6, #1F6FA0)',
                    }}
                  >
                    <Icon name="droplet" size={36} color="#FFFFFF" />
                  </div>
                </div>
                <h2 className="font-display font-semibold flex items-center justify-center gap-2" style={{ fontSize: 22, marginBottom: 18, color: '#0F1419' }}>
                  <Icon name={inputMode === "address" ? "home" : inputMode === "zip" ? "pin" : "city"} size={20} color="#51B0E6" />
                  {inputMode === "address" ? "Scanning your address" : inputMode === "zip" ? "Scanning ZIP code" : `Analyzing ${input}`}
                </h2>
                <div className="card-default overflow-hidden" data-testid="scan-steps" style={{ padding: 0 }}>
                  {SCAN_STEPS.map((msg, i) => {
                    const done = i < scanStep;
                    const active = i === scanStep;
                    return (
                      <div
                        key={i}
                        data-testid={`scan-step-${i}`}
                        className="flex items-center gap-2.5"
                        style={{
                          padding: '10px 14px',
                          background: done ? 'rgba(74, 138, 111, 0.06)' : active ? 'rgba(81, 176, 230, 0.06)' : 'transparent',
                          borderBottom: i < SCAN_STEPS.length - 1 ? '1px solid rgba(15, 20, 25, 0.04)' : 'none',
                          fontSize: 12,
                          color: done ? '#4A8A6F' : active ? '#51B0E6' : '#A6A8AB',
                          fontWeight: active ? 600 : 400,
                          transition: 'all 0.3s',
                        }}
                      >
                        {done ? (
                          <Icon name="check" size={16} color="#4A8A6F" />
                        ) : active ? (
                          <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #51B0E6', borderTopColor: 'transparent', animation: 'spin 1s linear infinite', display: 'inline-block' }} />
                        ) : (
                          <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(81, 176, 230, 0.25)', display: 'inline-block' }} />
                        )}
                        {msg}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {tbvView === "home" && phase === "select_utility" && (
              <UtilitySelector
                userLocation={utilitySelect?.user_location}
                candidates={utilitySelect?.candidates || []}
                selectingPwsid={selectingPwsid}
                errorMessage={selectError}
                onSelect={resolveUtility}
                onBack={() => {
                  setPhase("landing");
                  setUtilitySelect(null);
                  setSelectingPwsid(null);
                  setSelectError("");
                }}
              />
            )}

            {phase === "not_found" && (
              <div className="mx-auto" style={{ maxWidth: 360, margin: '40px auto', padding: '0 20px', textAlign: 'center' }} data-testid="not-found">
                <div
                  className="rounded-full flex items-center justify-center mx-auto"
                  style={{
                    width: 60,
                    height: 60,
                    background: 'rgba(200, 155, 60, 0.10)',
                    border: '2px solid rgba(200, 155, 60, 0.30)',
                    marginBottom: 16,
                  }}
                >
                  <Icon name="pin" size={28} color="#C89B3C" />
                </div>
                <h2 className="font-display font-semibold" style={{ fontSize: 22, marginBottom: 8, color: '#0F1419' }}>
                  We don't have data for this location yet
                </h2>
                <p style={{ marginBottom: 18, lineHeight: 1.6, fontSize: 14, color: '#3D4043' }}>
                  Enter your email and we'll notify you when data is available. We're adding 50 new cities monthly.
                </p>
                {!submitted ? (
                  <div className="mx-auto" style={{ maxWidth: 300 }}>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                      placeholder="Your email address"
                      error={emailError}
                      testId="not-found-email-input"
                      className="mb-2"
                    />
                    <button
                      type="button"
                      onClick={() => handleEmailSubmit('not_found')}
                      data-testid="not-found-submit-btn"
                      className="w-full btn-brand font-semibold rounded-card cursor-pointer"
                      style={{ padding: 12, fontSize: 13 }}
                    >
                      NOTIFY ME WHEN AVAILABLE {ARROW}
                    </button>
                  </div>
                ) : (
                  <div className="text-center" data-testid="not-found-success" style={{ padding: '10px 0' }}>
                    <div style={{ marginBottom: 5 }}>
                      <Icon name="checkCircle" size={28} />
                    </div>
                    <div className="font-semibold" style={{ fontSize: 13, color: '#4A8A6F' }}>You're on the list.</div>
                    <div style={{ fontSize: 11, marginTop: 4, color: '#6E7174' }}>We'll email you when data for this area is ready.</div>
                  </div>
                )}
                <div style={{ marginTop: 24 }}>
                  <div className="text-micro uppercase tracking-widest font-semibold" style={{ marginBottom: 8, fontSize: 10, color: '#6E7174' }}>
                    TRY A COVERED CITY
                  </div>
                  <div className="flex flex-wrap gap-1.5 justify-center">
                    {Object.keys(CITY_DATA).map((city) => (
                      <button
                        key={city}
                        type="button"
                        onClick={() => { setInput(city); setInputMode("city"); setPhase("landing"); setTimeout(() => startScan(city), 100); }}
                        className="font-semibold rounded-pill cursor-pointer"
                        style={{ padding: '6px 12px', fontSize: 11, background: 'rgba(81, 176, 230, 0.10)', color: '#1F6FA0', border: '1px solid rgba(81, 176, 230, 0.25)' }}
                      >
                        {city}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {tbvView === "scan" && (
              <div data-testid="bottle-tab">
                <BottleScanView
                  onBridge={() => { setScanHandoff(null); setTbvView("home"); setPhase("landing"); }}
                  initialBrand={scanHandoff ? scanHandoff.brandKey : null}
                  liveComparison={scanHandoff ? scanHandoff.live : null}
                  onHandoffConsumed={() => setScanHandoff(null)}
                />
              </div>
            )}
          </div>
        )}

        {tab === "wtr-intel" && (
          <div data-testid="insights-tab">
            <div className="sticky top-0 z-[50]" style={{ padding: '14px 16px 10px', background: '#FFFFFF', display: 'flex', justifyContent: 'center' }}>
              {/* Matches the Trust but Verify landing toggle: small, centered, icon pills */}
              <div style={{ width: '100%', maxWidth: 280 }}>
                <SegmentedToggle
                  testId="insights-toggle"
                  size="small"
                  value={insightView}
                  onChange={setInsightView}
                  options={[
                    { id: "report", label: "Report", badge: data ? riskScore : null, icon: <Icon name="pin" size={10} color={insightView === "report" ? "#51B0E6" : "#A6A8AB"} /> },
                    { id: "watch", label: "Watch", icon: <Icon name="bell" size={10} color={insightView === "watch" ? "#51B0E6" : "#A6A8AB"} /> },
                  ]}
                />
              </div>
            </div>

            {insightView === "report" && data && (
              <div style={{ padding: '14px 16px 20px' }} data-testid="report-tab">
                <HeroCard
                  testId="report-hero"
                  className="tbv-card mb-3"
                  eyebrow={`TRUST BUT VERIFY${TRADEMARK} REPORT`}
                  title={data.city}
                  subtitle={`${data.utility}. ${data.source}.`}
                  metadata={[
                    { label: "TDS",      value: data.tds ?? '-' },
                    { label: "pH",       value: data.ph ?? '-' },
                    { label: "Hardness", value: data.hardness ?? '-' },
                  ]}
                  rightRail={<RiskGauge score={riskScore} animated={gaugeOn} />}
                >
                  {input && inputMode !== "city" && (
                    <div className="flex items-center gap-1.5" style={{ fontSize: 12, color: '#6E7174' }}>
                      <Icon name={inputMode === "address" ? "home" : "pin"} size={12} color="#8A8E93" />
                      {inputMode === "address" ? input : `ZIP ${input}`}
                    </div>
                  )}
                </HeroCard>

                {highRisk.length > 0 && (
                  <div
                    className="card-critical flex items-start gap-2.5 mb-3"
                    style={{
                      padding: '11px 14px',
                      animation: 'slideUp 0.4s 0.1s ease forwards',
                      opacity: 0,
                    }}
                    data-testid="high-risk-alert"
                  >
                    <Icon name="hazard" size={18} color="#B84A4A" />
                    <div>
                      <div className="font-bold" style={{ fontSize: 11, marginBottom: 3, color: '#B84A4A' }}>
                        {highRisk.length} HIGH-CONCERN CONTAMINANT{highRisk.length > 1 ? "S" : ""} FOUND
                      </div>
                      <div style={{ fontSize: 12, color: '#3D4043', lineHeight: 1.5 }}>
                        {highRisk.map(c => c.name).join(" \u2022 ")}. Levels exceed health guidelines.
                      </div>
                    </div>
                  </div>
                )}

                {utilitySelect && (utilitySelect.candidates?.length || 0) > 1 && (
                  <UtilitySelector
                    variant="inline"
                    candidates={utilitySelect.candidates.filter(c => c.pwsid !== activePwsid)}
                    selectingPwsid={selectingPwsid}
                    errorMessage={selectError}
                    onSelect={resolveUtility}
                  />
                )}

                {reportOptInStatus === 'confirmed' ? (
                  <div className="card-default flex items-center gap-3 mb-3" style={{ padding: '12px 16px' }}>
                    <Icon name="checkCircle" size={22} />
                    <div className="flex-1">
                      <div className="font-semibold" style={{ fontSize: 13, color: '#0F1419' }}>Monthly Water Report. {cityShort}.</div>
                      <div style={{ fontSize: 11, marginTop: 2, color: '#6E7174' }}>Your report arrives the 1st of every month.</div>
                    </div>
                    <span className="text-micro uppercase tracking-wider font-bold" style={{ color: '#4A8A6F' }}>ENROLLED</span>
                  </div>
                ) : reportOptInStatus === 'pending' ? (
                  <div className="card-default flex items-center gap-3 mb-3" style={{ padding: '12px 16px' }}>
                    <div className="rounded-full flex items-center justify-center" style={{ width: 32, height: 32, background: 'rgba(200, 155, 60, 0.12)' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C89B3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold" style={{ fontSize: 13, color: '#0F1419' }}>Check Your Email</div>
                      <div style={{ fontSize: 11, marginTop: 2, color: '#6E7174' }}>Click the confirmation link sent to {reportOptInEmail}</div>
                    </div>
                    <button
                      type="button"
                      onClick={openReportModal}
                      className="font-bold uppercase tracking-wider cursor-pointer"
                      style={{ background: 'none', border: 'none', fontSize: 11, padding: 0, color: '#51B0E6' }}
                    >
                      RESEND
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={openReportModal}
                    className="w-full card-default flex items-center gap-3 mb-3 cursor-pointer text-left"
                    style={{ padding: '12px 16px' }}
                  >
                    <Icon name="bell" size={22} color="#51B0E6" />
                    <div className="flex-1">
                      <div className="font-semibold" style={{ fontSize: 13, color: '#0F1419' }}>Monthly Water Report. {cityShort}.</div>
                      <div style={{ fontSize: 11, marginTop: 2, color: '#6E7174' }}>Get your personalized report every month, free.</div>
                    </div>
                    <span
                      className="btn-brand font-bold uppercase tracking-wider rounded-card flex items-center justify-center"
                      style={{ padding: '7px 14px', fontSize: 11, minHeight: 36 }}
                    >
                      ENABLE
                    </span>
                  </button>
                )}

                <div className="text-micro uppercase tracking-widest font-semibold mb-2" style={{ fontSize: 10, color: '#6E7174' }}>
                  CONTAMINANTS IN {cityShort.toUpperCase()}
                </div>
                <div className="flex flex-col gap-2 mb-4" data-testid="contaminants-list">
                  {shownContaminants.map((c, i) => (
                    <div
                      key={c.name}
                      style={{ animation: `slideUp 0.4s ${i * 0.06}s ease forwards`, opacity: 0 }}
                    >
                      <ContaminantCard
                        testId={`contaminant-${c.name.toLowerCase().replace(/[()]/g, '').replace(/\s+/g, '-')}`}
                        name={c.name}
                        risk={c.risk}
                        category={c.category}
                        detail={c.detail}
                        level={typeof c.level !== 'object' ? c.level : null}
                        unit={c.unit}
                        limit={c.limit}
                        isViolation={c.isViolation}
                      />
                    </div>
                  ))}

                  {lowContaminants.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowLowContaminants(v => !v)}
                        data-testid="toggle-low-contaminants"
                        className="w-full card-default flex items-center justify-between cursor-pointer text-left"
                        style={{ padding: '12px 16px' }}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="rounded-pill font-bold uppercase tracking-wider"
                            style={{ background: 'rgba(74, 138, 111, 0.10)', color: '#4A8A6F', fontSize: 9, padding: '2px 8px' }}
                          >
                            LOW
                          </span>
                          <span className="font-semibold" style={{ fontSize: 13, color: '#0F1419' }}>
                            {lowContaminants.length} low-level detection{lowContaminants.length > 1 ? 's' : ''}
                          </span>
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#51B0E6' }}>
                          {showLowContaminants ? 'Hide' : 'Show'}
                        </span>
                      </button>

                      {showLowContaminants && lowContaminants.map((c, i) => (
                        <div
                          key={c.name}
                          style={{ animation: `slideUp 0.4s ${i * 0.06}s ease forwards`, opacity: 0 }}
                        >
                          <ContaminantCard
                            testId={`contaminant-${c.name.toLowerCase().replace(/[()]/g, '').replace(/\s+/g, '-')}`}
                            name={c.name}
                            risk={c.risk}
                            category={c.category}
                            detail={c.detail}
                            level={typeof c.level !== 'object' ? c.level : null}
                            unit={c.unit}
                            limit={c.limit}
                            isViolation={c.isViolation}
                          />
                        </div>
                      ))}
                    </>
                  )}
                </div>

                <HealthCalc city={data.city} riskScore={riskScore} />

                <HeroCard
                  className="mb-3"
                  eyebrow="THE SOLUTION"
                  title={`Trust but Verify${TRADEMARK} Your Water.`}
                  subtitle={`The Home WTR Hub removes every contaminant found in ${cityShort}'s water, at the tap, in real time.`}
                >
                  <a
                    href={shopifyHubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent('shopify_cta_clicked', { city: data?.city, risk_score: riskScore, discount_code: 'WELCOME100' })}
                    className="block w-full btn-brand text-center font-semibold rounded-card no-underline"
                    style={{ padding: '14px 20px', fontSize: 14 }}
                  >
                    GET THE HOME WTR HUB {ARROW}
                  </a>
                  <div className="flex flex-wrap gap-3 justify-center mt-3">
                    {["30-Day Guarantee", "30-Min Install", "Financing Available"].map((t) => (
                      <div key={t} className="flex items-center gap-1" style={{ fontSize: 12, color: '#6E7174' }}>
                        <Icon name="check" size={12} color="#4A8A6F" />
                        {t}
                      </div>
                    ))}
                  </div>
                </HeroCard>

                <div className="card-default overflow-hidden mb-3" style={{ padding: 0 }}>
                  <div className="flex items-center justify-between" style={{ padding: '12px 16px', borderBottom: '1px solid rgba(15, 20, 25, 0.04)' }}>
                    <div className="font-semibold" style={{ fontSize: 12, color: '#0F1419' }}>
                      Home WTR Hub. What Gets Removed.
                    </div>
                    <div className="text-micro uppercase tracking-widest font-semibold" style={{ fontSize: 10, color: '#1F6FA0' }}>{cityShort.toUpperCase()}</div>
                  </div>
                  {data.contaminants.map((c, i) => {
                    const rColor = c.risk === 'high' ? '#B84A4A' : c.risk === 'medium' ? '#C89B3C' : '#4A8A6F';
                    return (
                      <div
                        key={c.name}
                        className="grid items-center"
                        style={{
                          gridTemplateColumns: '1fr auto auto',
                          padding: '10px 16px',
                          gap: 8,
                          borderBottom: i < data.contaminants.length - 1 ? '1px solid rgba(15, 20, 25, 0.04)' : 'none',
                          background: i % 2 === 0 ? 'transparent' : 'rgba(81, 176, 230, 0.02)',
                        }}
                      >
                        <div>
                          <span className="font-semibold" style={{ fontSize: 12, color: '#0F1419' }}>{c.name}</span>
                          <span style={{ fontSize: 10, marginLeft: 8, color: '#6E7174' }}>{c.category}</span>
                        </div>
                        <div className="font-semibold" style={{ color: rColor, fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                          {c.level != null ? `${c.level} ${c.unit || ''}`.trim() : "Detected"}
                        </div>
                        <div
                          className="rounded-pill font-bold uppercase tracking-wider"
                          style={{ background: 'rgba(74, 138, 111, 0.10)', color: '#4A8A6F', fontSize: 9, padding: '2px 8px' }}
                        >
                          99%+ OUT
                        </div>
                      </div>
                    );
                  })}
                </div>

                {showHub && (
                  <div className="card-default mb-3" style={{ padding: 14 }} data-testid="wtr-hub-section">
                    <div className="flex items-center gap-2 mb-2.5">
                      <div className="rounded-card flex items-center justify-center" style={{ width: 30, height: 30, background: 'linear-gradient(135deg, #51B0E6, #1F6FA0)', color: '#FFFFFF', fontSize: 14 }}>HUB</div>
                      <div>
                        <div className="font-semibold" style={{ fontSize: 12, color: '#0F1419' }}>Generosity{TRADEMARK} Home WTR Hub</div>
                        <div style={{ fontSize: 10, color: '#1F6FA0' }}>Active Alkaline Technology. Multi-Stage.</div>
                      </div>
                    </div>
                    <WTRHubAnimation contaminants={data.contaminants} active={animating} />
                  </div>
                )}

                <div className="card-default mb-3" style={{ padding: 16 }} data-testid="email-capture">
                  <div className="font-display font-semibold" style={{ fontSize: 18, marginBottom: 4, color: '#0F1419' }}>
                    Get an extra $100 off the Home WTR Hub.
                  </div>
                  <div style={{ fontSize: 12, lineHeight: 1.5, marginBottom: 12, color: '#3D4043' }}>
                    Sale price $1,399.99. Your price $1,299.99 with code WELCOME100.
                  </div>
                  {!submitted ? (
                    <div className="flex flex-col gap-2">
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Your email address"
                        testId="email-input"
                      />
                      <button
                        type="button"
                        onClick={() => handleEmailSubmit('results')}
                        data-testid="submit-email-btn"
                        className="btn-brand font-semibold rounded-card cursor-pointer"
                        style={{ padding: 12, fontSize: 13 }}
                      >
                        GET MY $100 OFF {ARROW}
                      </button>
                      <div className="text-center" style={{ fontSize: 10, color: '#A6A8AB' }}>No spam. Unsubscribe anytime.</div>
                    </div>
                  ) : (
                    <EngagementFlow
                      engagementPhase={engagementPhase}
                      setEngagementPhase={setEngagementPhase}
                      householdProfile={householdProfile}
                      setHouseholdProfile={setHouseholdProfile}
                      data={data}
                      email={email}
                      capturedProspectId={capturedProspectId}
                      setPushResult={setPushResult}
                    />
                  )}
                </div>

                <div className="card-default flex items-center justify-between gap-2.5" style={{ padding: '12px 14px' }}>
                  <div>
                    <div className="font-semibold" style={{ fontSize: 12, color: '#0F1419' }}>Are you a Dealer or Distributor?</div>
                    <div style={{ fontSize: 10, marginTop: 2, color: '#6E7174' }}>Use Trust But Verify{TRADEMARK} as your sales tool.</div>
                  </div>
                  <a                  
                    href="https://generositywater.com/generosity-partners-paywall"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold uppercase tracking-wider rounded-card no-underline whitespace-nowrap"
                    style={{ padding: '8px 12px', fontSize: 10, background: 'rgba(81, 176, 230, 0.10)', color: '#1F6FA0', border: '1px solid rgba(81, 176, 230, 0.25)' }}
                  >
                    PARTNER PORTAL {ARROW}
                  </a>
                </div>
              </div>
            )}

            {insightView === "report" && !data && (
              <div className="text-center" style={{ padding: '60px 20px' }} data-testid="report-empty">
                <div className="flex justify-center mb-3">
                  <span style={{ color: '#A6A8AB', display: 'inline-flex' }}>
                    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="12" width="4" height="9" rx="1"/>
                      <rect x="10" y="6" width="4" height="15" rx="1"/>
                      <rect x="17" y="2" width="4" height="19" rx="1"/>
                    </svg>
                  </span>
                </div>
                <h3 className="font-display font-semibold" style={{ fontSize: 22, marginBottom: 7, color: '#0F1419' }}>
                  No Report Yet
                </h3>
                <p style={{ marginBottom: 16, fontSize: 14, color: '#3D4043' }}>
                  Enter your address on the Home tab to generate your free water intelligence report.
                </p>
                <button
                  type="button"
                  onClick={() => { setTab("tbv"); setPhase("landing"); }}
                  data-testid="go-test-water-btn"
                  className="btn-brand font-semibold rounded-card cursor-pointer"
                  style={{ padding: '12px 22px', fontSize: 13 }}
                >
                  TEST MY WATER {ARROW}
                </button>
              </div>
            )}

            {insightView === "watch" && (
              <div style={{ padding: '14px 16px 20px' }} data-testid="watch-tab">
                <WaterWatchFeed
                  zip={data?.zip || (/^\d{5}$/.test((input || '').trim()) ? input.trim() : null)}
                  pwsid={activePwsid}
                  utility={data?.utility}
                  city={cityShort}
                  contaminants={data?.contaminants || []}
                  hasReport={!!data}
                  onScan={() => { setTab("tbv"); setPhase("landing"); }}
                  onEnableAlerts={async () => {
                    trackEvent('watch_alerts_enabled', { zip: data?.zip, pwsid: activePwsid });
                    const result = await requestPushPermission(data?.zip || null, capturedProspectId, {});
                    setPushResult(result);
                    trackEvent(result?.granted ? 'push_permission_granted' : 'push_permission_denied', { source: 'water_watch' });
                  }}
                />
              </div>
            )}
          </div>
        )}

        {tab === "my-hydration" && <MyHydrationScreen onOpenProfile={() => setShowProfile(true)} onMenu={() => setDrawerOpen(true)} />}
        {tab === "my-intake" && <MyIntakeScreen onMenu={() => setDrawerOpen(true)} onSignIn={() => setShowAuth(true)} onOpenProfile={() => setShowProfile(true)} onUpgrade={() => setShowPaywall(true)} />}
        {/* Plain conditional render again. The BLE link, poll loop, and intake
            episode detection all live in lib/bleManager (module scope), so this
            screen can unmount freely without dropping the bottle. The old
            always-mounted display:none wrapper is gone: it kept a 1,800-line
            hidden screen re-rendering on every 2 s telemetry frame and kept its
            summary poll running on every tab. */}
        {tab === "wtr-btl" && (
          <WTRBottleScreen
            onMenu={() => setDrawerOpen(true)}
            onOpenBottleScan={(brandKey, live) => {
              setScanHandoff({ brandKey, live: live || null });
              setTbvView("scan");
              setTab("tbv");
            }}
          />
        )}
        {tab === "wtr-hub" && <WTRHubScreen onMenu={() => setDrawerOpen(true)} />}
      </div>

      {isGoogleLanding && (
        <button
          type="button"
          data-testid="hamburger-floating"
          onClick={() => setDrawerOpen(true)}
          aria-label="Menu"
          className="fixed flex items-center justify-center cursor-pointer z-[120]"
          style={{ top: 'calc(env(safe-area-inset-top) + 12px)', left: 'max(16px, calc(50% - 240px + 16px))', width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)', border: '1px solid rgba(15, 20, 25, 0.06)', boxShadow: '0 2px 8px rgba(15, 20, 25, 0.08)' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><line x1="4" y1="7" x2="20" y2="7" stroke="#0F1419" strokeWidth="1.6" strokeLinecap="round"/><line x1="4" y1="12" x2="20" y2="12" stroke="#0F1419" strokeWidth="1.6" strokeLinecap="round"/><line x1="4" y1="17" x2="20" y2="17" stroke="#0F1419" strokeWidth="1.6" strokeLinecap="round"/></svg>
        </button>
      )}

      <div
        className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full glass-nav flex z-[200]"
        style={{
          maxWidth: 480,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        data-testid="bottom-nav"
      >
        {navTabs.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => { setTab(t.id); if (t.id === "tbv") setPhase("landing"); }}
              data-testid={`nav-${t.id}`}
              className="flex-1 flex flex-col items-center gap-1 cursor-pointer relative"
              style={{
                background: 'transparent',
                border: 'none',
                padding: '10px 4px 12px',
                transition: 'opacity 0.15s',
              }}
            >
              {t.badge != null && t.badge > 0 && (
                <div
                  className="absolute rounded-pill flex items-center justify-center font-bold"
                  style={{
                    top: 7,
                    right: 'calc(50% - 18px)',
                    minWidth: 16,
                    height: 16,
                    background: t.badge > 66 ? '#B84A4A' : t.badge > 33 ? '#C89B3C' : '#4A8A6F',
                    fontSize: 9,
                    padding: '0 4px',
                    border: '1.5px solid #FFFFFF',
                    fontVariantNumeric: 'tabular-nums',
                    color: '#FFFFFF',
                  }}
                >
                  {t.badge}
                </div>
              )}
              {t.center ? (
                <div style={{ width: 46, height: 46, borderRadius: '50%', background: 'linear-gradient(135deg,#51B0E6,#1F6FA0)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: -20, boxShadow: '0 6px 16px rgba(81,176,230,0.4)', border: '3px solid #FFFFFF' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 3C12 3 6 10 6 14C6 17.31 8.69 20 12 20C15.31 20 18 17.31 18 14C18 10 12 3 12 3Z" stroke="#FFFFFF" strokeWidth="1.6" fill="rgba(255,255,255,0.18)"/></svg>
                </div>
              ) : (
                <NavIcon id={t.id} active={active} />
              )}
              <div
                className="text-center"
                style={{
                  fontSize: 10,
                  fontWeight: active ? 600 : 500,
                  color: active ? '#51B0E6' : '#8A8E93',
                  letterSpacing: '0.02em',
                  lineHeight: 1.2,
                }}
              >
                {t.label}
              </div>
              {active && (
                <div
                  className="absolute rounded-full"
                  style={{
                    bottom: 3,
                    width: 4,
                    height: 4,
                    background: '#51B0E6',
                    boxShadow: '0 0 6px #51B0E6',
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      <MonthlyReportModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        screen={reportModalScreen}
        email={reportEmail}
        onEmailChange={(v) => { setReportEmail(v); if (reportEmailError) setReportEmailError(''); }}
        emailError={reportEmailError}
        zip={reportZip}
        onZipChange={(v) => { setReportZip(v); if (reportZipError) setReportZipError(''); }}
        zipError={reportZipError}
        submitting={reportSubmitting}
        submitError={reportSubmitError}
        onSubmit={handleReportSubscribe}
        cityShort={cityShort}
      />

      {showProfile && (
        <div className="fixed inset-0 z-[500] overflow-y-auto" style={{ animation: 'slideUp 0.3s ease', background: '#FFFFFF' }}>
          <ProfileScreen onClose={() => setShowProfile(false)} />
        </div>
      )}

      {showIntake && (
        <div className="fixed inset-0 z-[500] overflow-y-auto" style={{ animation: 'slideUp 0.3s ease', background: '#FFFFFF' }}>
          <MyIntake onClose={() => setShowIntake(false)} />
        </div>
      )}

      {showDevices && (
        <div className="fixed inset-0 z-[500] overflow-y-auto" style={{ animation: 'slideUp 0.3s ease', background: '#FFFFFF' }}>
          <DevicesScreen onClose={() => setShowDevices(false)} />
        </div>
      )}

      {showHealth && (
        <div className="fixed inset-0 z-[500] overflow-y-auto" style={{ animation: 'slideUp 0.3s ease', background: '#FFFFFF' }}>
          <MyHealth onClose={() => setShowHealth(false)} />
        </div>
      )}

      {showReports && (
        <div className="fixed inset-0 z-[500] overflow-y-auto" style={{ animation: 'slideUp 0.3s ease', background: '#FFFFFF' }}>
          <ReportsScreen onClose={() => setShowReports(false)} />
        </div>
      )}

      {showHelp && (
        <div className="fixed inset-0 z-[500] overflow-y-auto" style={{ animation: 'slideUp 0.3s ease', background: '#FFFFFF' }}>
          <HelpScreen onClose={() => setShowHelp(false)} />
        </div>
      )}

      {showShop && (
        <div className="fixed inset-0 z-[500] overflow-y-auto" style={{ animation: 'slideUp 0.3s ease', background: '#FFFFFF' }}>
          <ShopScreen onClose={() => setShowShop(false)} />
        </div>
      )}

      {showAuth && (
        <div className="fixed inset-0 z-[500] overflow-y-auto" style={{ animation: 'slideUp 0.3s ease', background: '#FFFFFF' }}>
          <AuthScreen onClose={() => setShowAuth(false)} />
        </div>
      )}

      {authFlash && (
        <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 600, background: '#1A2535', color: '#fff', padding: '10px 18px', borderRadius: 999, fontSize: 13, fontWeight: 700, boxShadow: '0 6px 20px rgba(0,0,0,0.2)', maxWidth: '90%', textAlign: 'center' }}>
          {authFlash}
        </div>
      )}

      <NavDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onNavigate={(id) => { if (id === 'profile') setShowProfile(true); else if (id === 'intake') setShowIntake(true); else if (id === 'devices') setShowDevices(true); else if (id === 'health') setShowHealth(true); else if (id === 'reports') setShowReports(true); else if (id === 'help') setShowHelp(true); else if (id === 'shop') setShowShop(true); else if (id === 'signin') setShowAuth(true); }}
      />
      {showPaywall && <PaywallScreen onClose={() => setShowPaywall(false)} />}
    </div>
  );
}

// --- ENGAGEMENT FLOW (post-email-capture) ------------------------------------
function EngagementFlow({ engagementPhase, setEngagementPhase, householdProfile, setHouseholdProfile, data, email, capturedProspectId, setPushResult }) {
  return (
    <div className="text-center" data-testid="engagement-flow" style={{ padding: '12px 0' }}>
      {engagementPhase === "idle" && (
        <div>
          <div style={{ marginBottom: 5 }}>
            <Icon name="checkCircle" size={28} />
          </div>
          <div className="font-semibold" style={{ fontSize: 13, color: '#4A8A6F' }}>Report sent. Check your email.</div>
          <div style={{ fontSize: 11, marginTop: 4, color: '#6E7174' }}>Discount code: WELCOME100</div>
        </div>
      )}

      {engagementPhase === "push_prompt" && (
        <div className="mx-auto" style={{ maxWidth: 320, padding: '4px 0' }} data-testid="push-prompt">
          <div className="rounded-full flex items-center justify-center mx-auto" style={{ width: 44, height: 44, background: 'rgba(81, 176, 230, 0.10)', border: '2px solid rgba(81, 176, 230, 0.30)', marginBottom: 10 }}>
            <Icon name="alert" size={20} color="#51B0E6" />
          </div>
          <div className="font-semibold" style={{ fontSize: 14, marginBottom: 4, color: '#0F1419' }}>Water quality changes. You should know.</div>
          <div style={{ fontSize: 12, lineHeight: 1.5, marginBottom: 12, color: '#3D4043' }}>
            Get an alert when contamination levels change in your area. We only notify when it matters.
          </div>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={async () => {
                trackEvent('push_permission_requested', { city: data?.city });
                const result = await requestPushPermission(data?.zip || data?.city?.match(/\d{5}/)?.[0], capturedProspectId, {});
                setPushResult(result);
                trackEvent(result.granted ? 'push_permission_granted' : 'push_permission_denied', { city: data?.city });
                setEngagementPhase("household");
              }}
              className="btn-brand font-semibold rounded-card cursor-pointer"
              style={{ padding: '9px 20px', fontSize: 12 }}
            >
              ENABLE ALERTS
            </button>
            <button
              type="button"
              onClick={() => { trackEvent('push_permission_dismissed', { city: data?.city }); setEngagementPhase("household"); }}
              className="font-semibold rounded-card cursor-pointer"
              style={{ background: 'transparent', padding: '9px 16px', fontSize: 12, border: '1px solid rgba(15, 20, 25, 0.06)', color: '#6E7174' }}
            >
              Not now
            </button>
          </div>
        </div>
      )}

      {engagementPhase === "household" && (
        <div className="mx-auto text-left" style={{ maxWidth: 320, padding: '4px 0' }} data-testid="household-profile">
          <div className="font-semibold text-center" style={{ fontSize: 14, marginBottom: 3, color: '#0F1419' }}>
            Personalize your risk assessment
          </div>
          <div className="text-center mb-3" style={{ fontSize: 12, lineHeight: 1.5, color: '#3D4043' }}>
            Quick questions to tailor your report to your household.
          </div>
          {[
            { key: 'has_children', label: 'Children under 12 at home?', options: [{ label: 'Yes', val: true }, { label: 'No', val: false }] },
            { key: 'is_pregnant',  label: 'Anyone pregnant or trying to conceive?', options: [{ label: 'Yes', val: true }, { label: 'No', val: false }] },
            { key: 'has_filter',   label: 'Water filter currently installed?', options: [{ label: 'Yes', val: true }, { label: 'No', val: false }, { label: 'Not sure', val: 'unsure' }] },
          ].map((q) => (
            <div key={q.key} className="mb-3">
              <div className="font-semibold mb-1.5" style={{ fontSize: 11, color: '#3D4043' }}>{q.label}</div>
              <div className="flex gap-1.5">
                {q.options.map((opt) => {
                  const active = householdProfile[q.key] === opt.val;
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => setHouseholdProfile((p) => ({ ...p, [q.key]: opt.val }))}
                      className="flex-1 font-bold rounded-card cursor-pointer"
                      style={{
                        padding: 8,
                        border: active ? '2px solid #51B0E6' : '1px solid rgba(15, 20, 25, 0.06)',
                        background: active ? 'rgba(81, 176, 230, 0.06)' : '#FFFFFF',
                        color: active ? '#1F6FA0' : '#4A4F56',
                        fontSize: 11,
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={async () => {
                trackEvent('household_profile_submitted', { ...householdProfile, city: data?.city });
                try {
                  await fetch(`${API_BASE}/api/wtr/capture`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': API_BEARER },
                    body: JSON.stringify({
                      email,
                      zip: data?.zip,
                      city: data?.city,
                      source: 'household_profile_update',
                      household_profile: householdProfile,
                    }),
                  });
                } catch (e) {}
                setEngagementPhase("complete");
              }}
              className="flex-1 btn-brand font-semibold rounded-card cursor-pointer"
              style={{ padding: 10, fontSize: 12 }}
            >
              PERSONALIZE MY REPORT
            </button>
            <button
              type="button"
              onClick={() => { trackEvent('household_profile_skipped', { city: data?.city }); setEngagementPhase("complete"); }}
              className="font-semibold rounded-card cursor-pointer"
              style={{ background: 'transparent', padding: '10px 12px', fontSize: 11, border: '1px solid rgba(15, 20, 25, 0.06)', color: '#6E7174' }}
            >
              Skip
            </button>
          </div>
        </div>
      )}

      {engagementPhase === "complete" && (
        <div data-testid="engagement-complete">
          <div style={{ marginBottom: 5 }}>
            <Icon name="checkCircle" size={28} />
          </div>
          <div className="font-semibold" style={{ fontSize: 13, color: '#4A8A6F' }}>You're all set.</div>
          <div style={{ fontSize: 11, marginTop: 4, color: '#6E7174' }}>
            Discount code: <span className="font-bold" style={{ color: '#0F1419' }}>WELCOME100</span>
          </div>
          {householdProfile.has_children && (
            <div
              className="rounded-card text-left mt-2"
              style={{ background: 'rgba(200, 155, 60, 0.08)', border: '1px solid rgba(200, 155, 60, 0.25)', padding: '8px 12px', fontSize: 11, color: '#7A5E1F', lineHeight: 1.5 }}
            >
              Children are especially vulnerable to lead and PFAS. The Home WTR Hub removes 99%+ of both.
            </div>
          )}
          {householdProfile.is_pregnant && (
            <div
              className="rounded-card text-left mt-2"
              style={{ background: 'rgba(184, 74, 74, 0.06)', border: '1px solid rgba(184, 74, 74, 0.20)', padding: '8px 12px', fontSize: 11, color: '#7A2A2A', lineHeight: 1.5 }}
            >
              PFAS bioaccumulate in breast milk. Filtration before and during pregnancy is critical.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
